'use client'

import { useForm, SubmitHandler } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '../../../components/common/PasswordInput'
import Link from 'next/link'
import Google from '@/components/icons/Google'
import { ContesterLogo } from '@/components/icons'
import { useAuth } from '@/services/providers/auth-provider'
import { useRouter, useSearchParams } from 'next/navigation'
import { TurnstileWidget } from '@/components/common/captcha'
import { useState } from 'react'
import Image from 'next/image'
import { env } from '@/env'
import { useOnboardingStore } from '@/stores/onboard-store'

interface LoginFormValues {
  username: string
  password: string
  turnstileToken: string
}

function LoginForm() {
  const router = useRouter()
  const { resetForm } = useOnboardingStore()

  const auth = useAuth()
  const form = useForm<LoginFormValues>({
    defaultValues: { username: '', password: '', turnstileToken: '' },
  })
  const searchParams = useSearchParams()

  const [turnstileFinished, setTurnstileFinished] = useState(false)

  const {
    setValue,
    setError,
    formState: { errors },
  } = form

  const onSubmit: SubmitHandler<LoginFormValues> = async (values) => {
    try {
      if (values.turnstileToken === '') {
        setError('turnstileToken', {
          type: 'manual',
          message: 'กรุณายืนยันตัวตนด้วย Cloudflare Turnstile',
        })
        return
      }

      await auth.signIn(values.username, values.password, values.turnstileToken)
    } catch (error) {
      console.error('Error signing in:', error)
    }

    form.reset()
  }

  return (
    <div className="flex items-center justify-center">
      <div className="mx-auto grid w-[350px] gap-6">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col items-center justify-center">
            {/* # Temp for Hackapoop contest
                # TODO: remove when contest is over */}
            {/* TODO: handle this conditional rendering more dynamically */}
            {!searchParams.get('navigateTo')?.includes('contest') ? (
              <Link href="/" className="block">
                <ContesterLogo />
              </Link>
            ) : searchParams
                .get('navigateTo')
                ?.includes(
                  `contest/${env.NEXT_PUBLIC_HACKAPOOP_CONTEST_ID}`
                ) ? (
              <div className="flex w-full max-w-[300px] items-center justify-center pr-3">
                <Image
                  src="/assets/images/hackapoop/logo.svg"
                  loading="lazy"
                  alt="Hackapoop"
                  width={75}
                  height={22}
                  className="pt h-full"
                />
              </div>
            ) : (
              <div className="flex w-full max-w-[300px] justify-between pr-3">
                {/* <Image
                  src="/assets/images/bts/logoGroup.svg"
                  loading="lazy"
                  alt="Beyond The Screen x เทพลีลา"
                  width={300}
                  height={90}
                  className="pt h-auto w-full"
                /> */}
              </div>
            )}

            {/* # Temp for Hackapoop contest
                # TODO: remove when contest is over */}
            {/* TODO: handle this more dynamically */}
            <h1 className="text-5 mt-4 whitespace-pre text-center text-lg font-bold">
              {searchParams.get('navigateTo')?.includes('contest')
                ? searchParams
                    .get('navigateTo')
                    ?.includes(
                      `contest/${env.NEXT_PUBLIC_HACKAPOOP_CONTEST_ID}`
                    )
                  ? 'เข้าสู่โครงการ Hack A Poop\nผ่าน Contester.life'
                  : 'เข้าสู่โครงการ\nผ่าน Contester.life'
                : 'ลงทะเบียน'}
            </h1>
          </div>
          <div className="mt-4 grid gap-2">
            <div className="grid gap-2">
              <Input
                id="username"
                type="username"
                placeholder="ชื่อผู้ใช้งานหรืออีเมล"
                tabIndex={1}
                {...form.register('username')}
              />
              {errors.username && (
                <p className="text-sm text-red-600">
                  {errors.username.message}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <PasswordInput
                id="password"
                placeholder="รหัสผ่าน"
                tabIndex={2}
                {...form.register('password')}
              />
              {errors.password && (
                <p className="text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>
          <div className="mt-3 grid w-full gap-2">
            <div className="flex w-full justify-center">
              <TurnstileWidget
                onVerify={(token) => {
                  setValue('turnstileToken', token)
                  setTurnstileFinished(true)
                }}
              />
            </div>

            <div className="flex items-center">
              <Link
                href="/forgot-password"
                className="text-zinc500 ml-auto inline-block text-[10px] underline"
                tabIndex={6}
                onClick={(e) => {
                  e.preventDefault()
                }}
              >
                Forgot your password?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={auth.loading || !turnstileFinished}
              className="w-full rounded-full"
              tabIndex={3}
            >
              เข้าสู่ระบบ
            </Button>
          </div>
        </form>
        <div className="grid gap-2">
          <div className="text-3 text-zinc500 mt-4 text-center">
            ยังไม่ได้เป็นสมาชิก?{' '}
            <button
              onClick={(e) => {
                e.preventDefault()
                resetForm()
                router.push(
                  `/register${
                    searchParams.get('navigateTo')
                      ? `?${new URLSearchParams({ navigateTo: searchParams.get('navigateTo')! }).toString()}`
                      : ''
                  }`
                )
              }}
              className="underline"
              tabIndex={5}
            >
              คลิกเพื่อลงทะเบียน
            </button>
          </div>
        </div>
        <div className="relative my-4">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">
              OR CONTINUE WITH
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <Button
            onClick={(e) => {
              e.preventDefault()
              auth.signInWithGoogle()
            }}
            className="flex w-full items-center justify-center bg-white text-zinc-950 hover:bg-gray-100"
          >
            <Google />
            <span>Google</span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default LoginForm
